import { oc } from 'ts-optchain';

import { core } from '@tock/coretypes';
import { dashboard } from 'types';
import { getNotificationSoundsEnabled } from 'utils/service/localStorageUtils';

import { forEach } from 'es-toolkit/compat';
import { logger } from '@tock/logging';
import AppConstants from 'constants/AppConstants';

export enum TockNotificationType {
  NEW_ORDER,
  ORDER_READY_FOR_PREP,
}

export class TockNotification {
  notificationType: TockNotificationType;
  purchase: core.PastPurchase;

  constructor(notificationType: TockNotificationType, purchase: core.PastPurchase) {
    this.notificationType = notificationType;
    this.purchase = purchase;
  }

  public static id(notificationType: TockNotificationType, purchase: core.IPastPurchase) {
    return `${notificationType}-${purchase.id}`;
  }

  static NewOrder(purchase: core.PastPurchase) {
    return new TockNotification(TockNotificationType.NEW_ORDER, purchase);
  }

  static OrderReadyForPrep = (purchase: core.PastPurchase) => {
    return new TockNotification(TockNotificationType.ORDER_READY_FOR_PREP, purchase);
  };

  public id() {
    return TockNotification.id(this.notificationType, this.purchase);
  }
}

export type KeyedTockNotifications = { [index: string]: TockNotification };

interface State {
  autoPrintChitsStatus?: { message: string; healthy: boolean };
  notificationSoundsEnabledForDevice: boolean;
  purchases: NumericDictionary<core.PastPurchase>;
  activeNotifications: KeyedTockNotifications;
  dismissedNotifications: KeyedTockNotifications;
}

export const initialState: State = {
  autoPrintChitsStatus: undefined,
  notificationSoundsEnabledForDevice: getNotificationSoundsEnabled(),
  purchases: {},
  activeNotifications: {},
  dismissedNotifications: {},
};

function updatePurchase(
  purchase: core.PastPurchase,
  purchases: NumericDictionary<core.PastPurchase>
) {
  if (!purchase.originalPurchaseId) {
    return;
  }
  if (purchase.transferred) {
    delete purchases[purchase.originalPurchaseId];
    return;
  }
  purchases[purchase.originalPurchaseId] = purchase as core.PastPurchase;
}

export default function reducer(state: State = initialState, action: BaseTockAction): State {
  const { payload } = action;
  switch (action.type) {
    // should we get this from calendar entity update if its the same as today?
    // Copied from calendar.ts and pruned down
    case AppConstants.ORDER_NOTIFICATIONS_CALENDAR_ENTITY_UPDATE: {
      const changeUpdates = payload as dashboard.CalendarDayEntityUpdates;
      if (oc(changeUpdates).update[0].type() === 'INIT') {
        const purchases: NumericDictionary<core.PastPurchase> = {};

        forEach(oc(changeUpdates).update[0].ticketGroup(), (ticketGroup) => {
          forEach(ticketGroup.purchaseDetail as core.PastPurchase[], (purchase) => {
            updatePurchase(purchase, purchases);
          });
        });
        forEach(oc(changeUpdates).update[0].purchase() as core.PastPurchase[], (purchase) => {
          updatePurchase(purchase, purchases);
        });
        return {
          ...state,
          purchases,
        };
      } else {
        const purchases: NumericDictionary<core.PastPurchase> = state.purchases;
        forEach(changeUpdates.update, (update) => {
          if (update.type === 'REPLACE') {
            forEach(update.ticketGroup as core.TicketGroup[], (ticketGroup) => {
              forEach(ticketGroup.purchaseDetail as core.PastPurchase[], (purchase) => {
                updatePurchase(purchase, purchases);
              });
            });

            forEach(update.purchase as core.PastPurchase[], (purchase) => {
              updatePurchase(purchase, purchases);
            });
          } else if (update.type === 'REMOVE') {
            // Nothing to do
          } else {
            logger.error('Encountered unsupported update type');
          }
        });
        return {
          ...state,
          purchases: {
            ...purchases,
          },
        };
      }
    }

    case AppConstants.AUTO_PRINT_CHITS_CHIT_PRINTED: {
      const purchaseId = payload as number;
      const purchaseVisit = state.purchases[purchaseId]?.visit;
      if (purchaseVisit) {
        purchaseVisit.chitPrinted = true;
      }
      return state;
    }

    case AppConstants.AUTO_PRINT_CHITS_STATUS: {
      return {
        ...state,
        autoPrintChitsStatus: payload,
      };
    }

    case AppConstants.ORDER_NOTIFICATION_SOUNDS_ENABLED: {
      return {
        ...state,
        notificationSoundsEnabledForDevice: payload,
      };
    }

    case AppConstants.ORDER_NOTIFICATIONS_SOUND_PLAYED: {
      const purchaseId = payload.purchaseId as number;
      const purchaseVisit = state.purchases[purchaseId]?.visit;
      if (purchaseVisit) {
        if (payload.forCreation) {
          purchaseVisit.creationNotificationSoundPlayed = true;
        } else {
          purchaseVisit.notificationSoundPlayed = true;
        }
      }
      return state;
    }

    case AppConstants.ORDER_NOTIFICATIONS_QUEUE_ALERT: {
      const notification = payload as TockNotification;
      return {
        ...state,
        activeNotifications: {
          ...state.activeNotifications,
          [notification.id()]: notification,
        },
      };
    }

    case AppConstants.ORDER_NOTIFICATIONS_DISMISS_ALERT: {
      const { purchaseId, notificationType } = payload as {
        purchaseId: number;
        notificationType: TockNotificationType;
      };
      const idToRemove = TockNotification.id(notificationType, { id: purchaseId });
      const notificationToRemove = state.activeNotifications[idToRemove];
      const newActiveNotifications = { ...state.activeNotifications };
      delete newActiveNotifications[idToRemove];

      return {
        ...state,
        activeNotifications: newActiveNotifications,
        dismissedNotifications: {
          ...state.dismissedNotifications,
          [idToRemove]: notificationToRemove,
        },
      };
    }

    default:
      return state;
  }
}
